import React, { createContext, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { t } from '../utils/translations';
import {
  assistantKnowledgeBase,
  assistantKeywords,
  assistantQuickSuggestions
} from '../data/assistantKnowledgeBase';
import { assistantFacts } from '../data/assistantFacts';
import { formatAssistantText, WHATSAPP_AGENT_DISPLAY } from '../utils/assistantFormat';

const AIAssistantContext = createContext();

export function useAIAssistant() {
  return useContext(AIAssistantContext);
}

const knowledgeBase = assistantKnowledgeBase;
const keywords = assistantKeywords;
const quickSuggestionsData = assistantQuickSuggestions;
const facts = assistantFacts;

/** Score une fiche fact par chevauchement de mots-clés avec la question */
const scoreFact = (questionLower, fact) => {
  if (!fact?.keywords?.length) return 0;
  let score = 0;
  let matched = 0;
  for (const keyword of fact.keywords) {
    const k = keyword.toLowerCase().trim();
    if (!k) continue;
    if (questionLower.includes(k)) {
      matched += 1;
      score += Math.min(k.length, 24) + (k.includes(' ') ? 8 : 0);
      continue;
    }
    const parts = k.split(/\s+/).filter((p) => p.length > 2);
    if (parts.length >= 2 && parts.every((p) => questionLower.includes(p))) {
      matched += 1;
      score += Math.min(k.length, 20) + 4;
    }
  }
  if (matched === 0) return 0;
  return score + matched * 4;
};

const findBestFact = (question, lang) => {
  const list = facts && facts[lang] ? facts[lang] : facts?.fr || [];
  if (!list.length) return null;
  const questionLower = question.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const fact of list) {
    const s = scoreFact(questionLower, fact);
    if (s > bestScore) {
      bestScore = s;
      best = fact;
    }
  }
  if (!best || bestScore < 5) return null;
  return { fact: best, score: bestScore };
};

const scoreCategory = (questionLower, lang) => {
  const categoryKeywords = (keywords && keywords[lang] ? keywords[lang] : keywords?.fr) || {};
  let bestCat = null;
  let bestScore = 0;
  for (const [category, words] of Object.entries(categoryKeywords)) {
    if (!Array.isArray(words)) continue;
    let score = 0;
    for (const word of words) {
      const w = String(word || '').toLowerCase().trim();
      if (!w) continue;
      if (questionLower.includes(w)) score += Math.min(w.length, 16) + 3;
    }
    if (score > bestScore) {
      bestScore = score;
      bestCat = category;
    }
  }
  return bestScore >= 10 ? { category: bestCat, score: bestScore } : null;
};

export function AIAssistantProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Données chargées depuis assistantKnowledgeBase.js

  // Détecter la langue de la question
  const detectLanguage = (text) => {
    const englishWords = ['hello', 'hi', 'how', 'what', 'where', 'when', 'why', 'the', 'and', 'or', 'please', 'can'];
    const frenchWords = ['bonjour', 'salut', 'comment', 'que', 'quoi', 'où', 'quand', 'pourquoi', 'le', 'la', 'et', 'ou', 'merci', 'pouvez'];
    
    const words = text.toLowerCase().split(/\s+/);
    const englishCount = words.filter((word) => englishWords.includes(word)).length;
    const frenchCount = words.filter((word) => frenchWords.includes(word)).length;
    
    return englishCount > frenchCount ? 'en' : 'fr';
  };

  // Rechercher sur Wikipedia (uniquement questions factuelles Guinée / Conakry)
  const searchWikipedia = async (query, lang = 'fr') => {
    try {
      console.log('🔍 Recherche Wikipedia pour:', query);
      // Nettoyer la query : enlever les mots courants et garder les mots-clés importants
      const stopWords = lang === 'fr' 
        ? ['le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'et', 'ou', 'où', 'est', 'sont', 'quel', 'quelle', 'comment', 'quoi', 'pourquoi', 'combien', 'qui', 'que', 'dans', 'sur', 'avec', 'pour', 'par']
        : ['the', 'a', 'an', 'and', 'or', 'where', 'is', 'are', 'what', 'how', 'why', 'how many', 'who', 'which', 'in', 'on', 'with', 'for', 'by'];
      
      // Enlever la ponctuation et normaliser
      let cleanedQuery = query.toLowerCase()
        .replace(/[^\w\s\u00C0-\u017F]/g, ' ') // Garder les caractères accentués
        .replace(/\s+/g, ' ')
        .trim();
      
      // Extraire les mots importants
      const words = cleanedQuery
        .split(/\s+/)
        .filter(word => word.length > 2 && !stopWords.includes(word));
      
      if (words.length === 0) return null;
      
      // Si la question contient "Conakry" ou "Guinée", les inclure dans la recherche
      const hasConakry = cleanedQuery.includes('conakry');
      const hasGuinee = cleanedQuery.includes('guinée') || cleanedQuery.includes('guinee');
      
      // Construire le terme de recherche (priorité aux noms propres et lieux)
      let searchTerms = [];
      if (hasConakry) searchTerms.push('Conakry');
      if (hasGuinee) searchTerms.push('Guinée');
      
      // Détecter les questions sur les dirigeants
      const hasPresident = cleanedQuery.includes('président') || cleanedQuery.includes('president');
      const hasMinistre = cleanedQuery.includes('ministre') || cleanedQuery.includes('minister');
      
      let searchTerm;
      if (hasPresident && hasGuinee) {
        // Recherche spécifique pour président de la Guinée
        searchTerm = 'Président de la Guinée';
      } else if (hasMinistre && hasGuinee) {
        searchTerm = 'Premier ministre de la Guinée';
      } else {
        // Ajouter les autres mots-clés
        const otherWords = words.filter(w => !['conakry', 'guinée', 'guinee', 'président', 'president', 'ministre', 'minister'].includes(w));
        searchTerms = searchTerms.concat(otherWords.slice(0, 3));
        searchTerm = searchTerms.length > 0 ? searchTerms.join(' ') : words.slice(0, 3).join(' ');
      }
      
      // Utiliser l'API Wikipedia REST v1 pour chercher
      const wikiLang = lang === 'fr' ? 'fr' : 'en';
      
      // D'abord chercher la page exacte
      let wikiUrl = `https://${wikiLang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(searchTerm)}`;
      
      let response = await fetch(wikiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'ConakryEnPoche/1.0 (https://conakryenpoche.app; contact@example.com)'
        }
      });
      
      // Si pas trouvé, faire une recherche plus large
      if (!response.ok) {
        // Essayer avec OpenSearch pour trouver la page la plus pertinente
        const searchUrl = `https://${wikiLang}.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(searchTerm)}&limit=3&namespace=0&format=json`;
        const searchResponse = await fetch(searchUrl, {
          headers: {
            'User-Agent': 'ConakryEnPoche/1.0'
          }
        });
        
        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          console.log('🔍 OpenSearch résultats:', searchData);
          if (searchData[1] && searchData[1].length > 0) {
            const pageTitle = searchData[1][0];
            console.log('🔍 Tentative avec titre:', pageTitle);
            wikiUrl = `https://${wikiLang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(pageTitle)}`;
            response = await fetch(wikiUrl, {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'ConakryEnPoche/1.0'
              }
            });
          }
        }
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Wikipedia réponse reçue:', data.title);
        
        // Vérifier que c'est pertinent (pas une page de désambiguïsation)
        if (data.extract && data.extract.length > 50 && !data.title.includes('homonymie') && !data.title.includes('Homonymie')) {
          // Limiter la réponse à 500 caractères et ajouter un lien
          const extract = data.extract.substring(0, 500);
          const fullExtract = extract.length < data.extract.length ? extract + '...' : extract;
          
          const sourceLabel = lang === 'fr' ? 'Source' : 'Source';
          
          console.log('✅ Résultat Wikipedia trouvé:', data.title);
          return {
            text: `${fullExtract}\n\n📚 ${sourceLabel} : ${data.title} (Wikipedia)`,
            category: lang === 'fr' ? 'Recherche Wikipedia' : 'Wikipedia Search',
            language: lang,
            source: data.content_urls?.desktop?.page || data.content_urls?.mobile?.page
          };
        } else {
          console.log('⚠️ Wikipedia: extrait trop court ou page de désambiguïsation');
        }
      } else {
        console.log('⚠️ Wikipedia: page non trouvée, code:', response.status);
      }
      
      return null;
    } catch (error) {
      console.error('❌ Erreur recherche Wikipedia:', error);
      return null;
    }
  };

  // Questions encyclopédiques uniquement (pas les questions app / pratiques locales)
  const isFactualQuestion = (question, lang) => {
    const questionLower = question.toLowerCase();
    const appWords = [
      'carte cep', 'portefeuille', 'wallet', 'application', 'app', 'conakry en poche',
      'allo', 'réserver', 'reservation', 'réserver', 'billet', 'paiement', 'whatsapp',
      'mot de passe', 'password', 'compte', 'inscription'
    ];
    if (appWords.some((w) => questionLower.includes(w))) return false;

    const factualKeywords =
      lang === 'fr'
        ? [
            'président',
            'president',
            'premier ministre',
            'qui est',
            'population',
            'capitale',
            'histoire',
            'fondation',
            'république de guinée',
            'republique de guinee'
          ]
        : [
            'president',
            'prime minister',
            'who is',
            'population',
            'capital',
            'history',
            'founded',
            'republic of guinea'
          ];

    return factualKeywords.some((keyword) => questionLower.includes(keyword));
  };

  const handoffResponse = (lang, userQuestion) => ({
    text: formatAssistantText(t(lang, 'aiAssistantContext.whatsappMessage')),
    category: t(lang, 'aiAssistantContext.supportCategory'),
    language: lang,
    fromDatabase: false,
    needsWhatsApp: true,
    userQuestion: String(userQuestion || '').trim()
  });

  // Générer une réponse professionnelle (faits → catégorie fiable → wiki factuel → WhatsApp)
  const generateResponse = async (question, detectedLang = null) => {
    const lang = detectedLang || detectLanguage(question);
    const questionLower = String(question || '').toLowerCase();
    const langBase = knowledgeBase && knowledgeBase[lang] ? knowledgeBase[lang] : knowledgeBase?.fr || {};

    const matched = findBestFact(question, lang);
    if (matched?.fact) {
      const catMeta = langBase?.categories?.[matched.fact.category];
      return {
        text: formatAssistantText(matched.fact.answer),
        category: catMeta?.name || matched.fact.category,
        language: lang,
        fromDatabase: true,
        factId: matched.fact.id
      };
    }

    const catHit = scoreCategory(questionLower, lang);
    if (catHit) {
      const categoryData = langBase?.categories?.[catHit.category];
      if (categoryData?.responses?.length) {
        // Réponse stable (pas aléatoire) + invitation discrète si besoin d’un humain
        const stable = categoryData.responses[0];
        return {
          text: formatAssistantText(
            `${stable}\n\n${
              lang === 'en'
                ? `If you need a human agent, WhatsApp us at ${WHATSAPP_AGENT_DISPLAY}.`
                : `Pour parler à un agent, WhatsApp : ${WHATSAPP_AGENT_DISPLAY}.`
            }`
          ),
          category: categoryData.name || catHit.category,
          language: lang,
          fromDatabase: true,
          needsWhatsApp: true,
          userQuestion: String(question || '').trim()
        };
      }
    }

    if (isFactualQuestion(question, lang)) {
      const wikiResult = await searchWikipedia(question, lang);
      if (wikiResult) {
        return {
          ...wikiResult,
          text: formatAssistantText(wikiResult.text)
        };
      }
    }

    return handoffResponse(lang, question);
  };

  // Envoyer un message
  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: message,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentMessage('');
    setIsTyping(true);

    try {
      await new Promise((r) => setTimeout(r, 350 + Math.min(900, message.length * 8)));
      const response = await generateResponse(message);

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: response.text,
        category: response.category,
        language: response.language,
        source: response.source || null,
        fromDatabase: response.fromDatabase || false,
        needsWhatsApp: response.needsWhatsApp || false,
        userQuestion: response.userQuestion || message,
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, botMessage]);

      if (isVoiceEnabled && 'speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(response.text);
        utterance.lang = response.language === 'fr' ? 'fr-FR' : 'en-US';
        speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Erreur lors de la génération de la réponse:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: t(language, 'aiAssistantContext.errorMessage'),
        category: t(language, 'aiAssistantContext.errorCategory'),
        language,
        needsWhatsApp: true,
        userQuestion: message,
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Afficher le message de bienvenue professionnel
  const showWelcome = () => {
    if (hasShownWelcome) return;

    const welcomeBotMessage = {
      id: Date.now(),
      type: 'bot',
      text: formatAssistantText(t(language, 'aiAssistantContext.welcomeMessage')),
      category: t(language, 'aiAssistantContext.welcomeCategory'),
      language,
      timestamp: new Date(),
      isWelcome: true
    };

    setMessages([welcomeBotMessage]);
    setHasShownWelcome(true);
  };

  // Ouvrir l'assistant
  const openAssistant = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setTimeout(showWelcome, 500);
    }
  };

  // Fermer l'assistant
  const closeAssistant = () => {
    setIsOpen(false);
  };

  // Changer de langue
  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
    toast.success(t(newLang, 'aiAssistantContext.languageChanged'));
  };

  // Basculer la voix
  const toggleVoice = () => {
    setIsVoiceEnabled(!isVoiceEnabled);
    toast.success(t(language, !isVoiceEnabled ? 'aiAssistantContext.voiceEnabled' : 'aiAssistantContext.voiceDisabled'));
  };

  // Pas d'info-bulle auto : Nimba reste une aide discrète (FAQ), ouverte à la demande.

  const value = {
    isOpen,
    messages,
    currentMessage,
    setCurrentMessage,
    isTyping,
    language,
    isVoiceEnabled,
    sendMessage,
    openAssistant,
    closeAssistant,
    toggleLanguage,
    toggleVoice,
    quickSuggestions: quickSuggestionsData[language],
    showTooltip,
    setShowTooltip
  };

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}

